import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { ExchangeRates, CurrencyConversion } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

function extractErrorMessage(payload: unknown): string {
  if (typeof payload === 'string' && payload) return payload;
  if (payload && typeof payload === 'object') {
    // Direct message/error fields
    const obj = payload as { message?: unknown; error?: unknown; reason?: unknown; data?: unknown; issues?: unknown; name?: unknown };
    if (typeof obj.message === 'string' && obj.message) return obj.message;
    if (typeof obj.error === 'string' && obj.error) return obj.error;
    if (typeof obj.reason === 'string' && obj.reason) return obj.reason;
    // Nested error/message object: Hono envelope puts the ZodError at payload.error
    if (obj.error && typeof obj.error === 'object') {
      const nestedError = obj.error as { message?: unknown; name?: unknown; issues?: unknown };
      if (typeof nestedError.message === 'string' && nestedError.message) return nestedError.message;
      // Nested: payload.error.issues (ZodError shape)
      if (nestedError.name === 'ZodError' && Array.isArray(nestedError.issues) && nestedError.issues.length > 0) {
        const first = nestedError.issues[0] as { message?: unknown; path?: unknown };
        if (typeof first.message === 'string' && first.message) {
          const path = Array.isArray(first.path) && first.path.length > 0 ? first.path.join('.') + ': ' : '';
          return `${path}${first.message}`;
        }
      }
    }
    // Nested data.{message|error} shape (Axios + wrapped API envelope)
    if (obj.data && typeof obj.data === 'object') {
      const nested = obj.data as { message?: unknown; error?: unknown };
      if (typeof nested.message === 'string' && nested.message) return nested.message;
      if (typeof nested.error === 'string' && nested.error) return nested.error;
    }
    // ZodError shape: { name: 'ZodError', issues: [{ message, path, code }, ...] }
    if (obj.name === 'ZodError' && Array.isArray(obj.issues) && obj.issues.length > 0) {
      const first = obj.issues[0] as { message?: unknown; path?: unknown };
      if (typeof first.message === 'string' && first.message) {
        const path = Array.isArray(first.path) && first.path.length > 0 ? first.path.join('.') + ': ' : '';
        return `${path}${first.message}`;
      }
    }
  }
  return 'The server did not return a recognizable error. Please try again or contact support.';
}

interface CurrencyEvent {
  _id: string;
  user: string;
  fromCurrency: string;
  toCurrency: string;
  fxRate: number;
  fxRateProvider: string;
  recordsAffected: number;
  status: 'pending' | 'completed' | 'failed' | 'rolled_back';
  reason?: string;
  createdAt: string;
  updatedAt: string;
}

interface CurrencyState {
  rates: ExchangeRates | null;
  supportedCurrencies: string[];
  lastConversion: CurrencyConversion | null;
  events: CurrencyEvent[] | null;
  eventsPagination: { page: number; total: number; totalPages: number } | null;
  rollbackInProgress: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: CurrencyState = {
  rates: null,
  supportedCurrencies: [],
  lastConversion: null,
  events: null,
  eventsPagination: null,
  rollbackInProgress: false,
  isLoading: false,
  error: null,
};

export const fetchExchangeRates = createAsyncThunk(
  'currency/fetchRates',
  async (base: string | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.currency.rates, { params: base ? { base } : undefined });
      return (response.data.data || response.data) as ExchangeRates;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch exchange rates'));
    }
  }
);

export const convertCurrency = createAsyncThunk(
  'currency/convert',
  async (params: { from: string; to: string; amount: number }, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.currency.convert, { params });
      return (response.data.data || response.data) as CurrencyConversion;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to convert currency'));
    }
  }
);

export const fetchSupportedCurrencies = createAsyncThunk(
  'currency/fetchSupported',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.currency.supported);
      return (response.data.data || response.data) as string[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch currencies'));
    }
  }
);

export const fetchEvents = createAsyncThunk(
  'currency/fetchEvents',
  async (params: { page?: number; limit?: number; status?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.currency.events, { params });
      return response.data as { data: CurrencyEvent[]; page: number; total: number; totalPages: number };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch currency events'));
    }
  }
);

export const rollbackEvent = createAsyncThunk(
  'currency/rollbackEvent',
  async (params: { eventId: string; idempotencyKey: string; reason: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.currency.rollback(params.eventId), {
        idempotencyKey: params.idempotencyKey,
        reason: params.reason,
      });
      return (response.data.data ?? response.data) as CurrencyEvent;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to rollback currency event'));
    }
  }
);

const currencySlice = createSlice({
  name: 'currency',
  initialState,
  reducers: {
    clearCurrencyError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    // Rates
    builder.addCase(fetchExchangeRates.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchExchangeRates.fulfilled, (state, action) => { state.isLoading = false; state.rates = action.payload; });
    builder.addCase(fetchExchangeRates.rejected, (state, action) => { state.isLoading = false; state.error = extractErrorMessage(action.payload); });

    // Convert
    builder.addCase(convertCurrency.pending, (state) => { state.isLoading = true; });
    builder.addCase(convertCurrency.fulfilled, (state, action) => { state.isLoading = false; state.lastConversion = action.payload; });
    builder.addCase(convertCurrency.rejected, (state, action) => { state.isLoading = false; state.error = extractErrorMessage(action.payload); });

    // Supported
    builder.addCase(fetchSupportedCurrencies.fulfilled, (state, action) => { state.supportedCurrencies = action.payload; });

    // Events
    builder.addCase(fetchEvents.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchEvents.fulfilled, (state, action) => {
      state.isLoading = false;
      state.events = action.payload.data;
      state.eventsPagination = { page: action.payload.page, total: action.payload.total, totalPages: action.payload.totalPages };
    });
    builder.addCase(fetchEvents.rejected, (state, action) => { state.isLoading = false; state.error = extractErrorMessage(action.payload); });

    // Rollback
    builder.addCase(rollbackEvent.pending, (state) => { state.rollbackInProgress = true; state.error = null; });
    builder.addCase(rollbackEvent.fulfilled, (state) => { state.rollbackInProgress = false; });
    builder.addCase(rollbackEvent.rejected, (state, action) => { state.rollbackInProgress = false; state.error = extractErrorMessage(action.payload); });
  },
});

export const { clearCurrencyError } = currencySlice.actions;
export default currencySlice.reducer;